// INVARIANT: Onboarding is atomic — single RPC, no side effects before completion (see docs/invariants/PHASE_2_EXPERIENCE.md)
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useUser } from "@/contexts/user";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "next-themes";
import PixelAvatarSelector from "@/components/PixelAvatarSelector";
import PremiumUpgradeModal from "@/components/PremiumUpgradeModal";
import { CheckCircle, LogOut, Crown, Users, UserSquare2, AlertCircle, Target, Globe, Palette, Lock, ArrowLeft, Sparkles, RefreshCw, Trash2, AtSign, Check, X, Loader2 } from "lucide-react";
import { getAvatarUrl, getInitialsFromHandle } from "@/utils/avatars";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import { debug } from "@/utils/debug";
import { themeDebug } from "@/utils/themeDebug";
import { cn } from "@/lib/utils";
import { useHandleValidation, type HandleStatus } from "@/hooks/useHandleValidation";
import { isAutoGeneratedHandle, normalizeHandle, HANDLE_MIN_LENGTH, HANDLE_MAX_LENGTH } from "@/lib/handle";

// Theme definitions - single source of truth from shared config
import { THEMES, getFreeThemes, getPremiumThemes, getThemeById } from '@/config/themes';
import { ThemePreviewCard } from '@/components/onboarding/ThemePreviewCard';

export default function Onboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, updateUser } = useUser();
  const { user: authUser, setOnboardingCompleted } = useAuth();
  const { setTheme } = useTheme();
  const { toast } = useToast();
  
  // V4: Handle is always Step 1 - no conditional gate
  // Step 1 = Handle, Step 2 = Daily Goal, Step 3 = Timezone, Step 4 = Avatar, Step 5 = Theme, Step 6 = Summary
  const [step, setStep] = useState(1);
  const [stepDirection, setStepDirection] = useState<'forward' | 'backward'>('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  const [dailyGoalHours, setDailyGoalHours] = useState(2);
  const [dailyGoalMinutes, setDailyGoalMinutes] = useState(0);
  const [selectedAvatar, setSelectedAvatar] = useState(`default-${Date.now()}`);
  const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const [professionalMode, setProfessionalMode] = useState(true);
  const [socialMode, setSocialMode] = useState(user.study_profile === 'social');
  const [selectedTheme, setSelectedTheme] = useState('charcoal-pro');
  
  // Debug: Log social mode state changes
  useEffect(() => {
    debug.log('[Onboarding:SocialMode] State changed:', {
      socialMode,
      willSaveAs: socialMode ? 'social' : 'solo'
    });
  }, [socialMode]);
  const [isSaving, setIsSaving] = useState(false);
  const [handle, setHandle] = useState('');
  const [loadingHandle, setLoadingHandle] = useState(true);
  const [handleInputValue, setHandleInputValue] = useState('');
  // V4: Store original database handle to allow user to keep their own handle
  const [originalDbHandle, setOriginalDbHandle] = useState<string | undefined>(undefined);
  
  // V4: Use shared handle validation hook
  const { status: handleStatus, message: handleMessage, validate: validateHandle, reset: resetHandleValidation } = useHandleValidation();

  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // V4: Handle loading - just load handle for pre-fill, no conditional gating
  // NOTE: Per theme-architecture.md, we DO NOT force theme here
  // Theme is a device preference, not identity - user's previous choice remains active
  useEffect(() => {
    debug.log('[Onboarding:HandleLoad] Starting handle load, authUser:', authUser?.id);
    
    if (!authUser) {
      debug.log('[Onboarding:HandleLoad] No authUser, redirecting to /auth');
      navigate('/auth');
      return;
    }

    const loadHandle = async () => {
      debug.log('[Onboarding:HandleLoad] Loading handle for user:', authUser.id);
      
      try {
        // Query database for handle
        const { data, error } = await supabase
          .from('user_profiles')
          .select('handle, is_handle_final')
          .eq('id', authUser.id)
          .single();

        debug.log('[Onboarding:HandleLoad] Database query result:', { data, error });

        if (error) {
          // No profile yet - will be created during onboarding
          debug.log('[Onboarding:HandleLoad] No profile found, starting fresh');
          setLoadingHandle(false);
          return;
        }

        if (data?.handle) {
          setHandle(data.handle);
          setHandleInputValue(data.handle);
          setOriginalDbHandle(data.handle); // Store original for validation comparison
          debug.log('[Onboarding:HandleLoad] Loaded handle:', data.handle);
          
          // V4: If handle exists and is valid, pre-validate it so user can continue immediately
          validateHandle(data.handle, data.handle);
        }
        
      } catch (e) {
        debug.error('[Onboarding:HandleLoad] Error querying database:', e);
      }
      
      setLoadingHandle(false);
    };

    loadHandle();
  // validateHandle may not be referentially stable - use ref pattern
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, navigate]); // Removed validateHandle - called inside effect, not reactive dep

  const totalDailyGoal = dailyGoalHours * 60 + dailyGoalMinutes;
  const isPremiumGoal = totalDailyGoal > 480;

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
      navigate('/auth');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // V2: Removed handleRecoverySubmit - auth error UI should never write identity data
  // Handle authority chain: Database Trigger → Onboarding (display only) → Settings (edit)

  const nextStep = () => {
    setStepDirection('forward');
    setIsAnimating(true);
    setTimeout(() => {
      setStep(step + 1);
      setIsAnimating(false);
    }, 150);
  };

  const prevStep = () => {
    // V4: Step 1 (Handle) is minimum - no going back from there
    if (step <= 1) return;
    
    setStepDirection('backward');
    setIsAnimating(true);
    setTimeout(() => {
      setStep(step - 1);
      setIsAnimating(false);
    }, 150);
  };

  const completeOnboarding = async () => {
    const startTime = Date.now();
    debug.log('='.repeat(60));
    debug.log('[Onboarding] 🚀 completeOnboarding INVOKED at', new Date().toISOString());
    debug.log('[Onboarding] 📊 Current state:');
    debug.log('  - authUser?.id:', authUser?.id);
    debug.log('  - authUser?.email:', authUser?.email);
    debug.log('  - handle:', handle, '(length:', handle?.length, ')');
    debug.log('  - selectedTimezone:', selectedTimezone);
    debug.log('  - selectedAvatar:', selectedAvatar);
    debug.log('  - selectedTheme:', selectedTheme);
    debug.log('  - totalDailyGoal:', totalDailyGoal);
    debug.log('  - professionalMode:', professionalMode);
    debug.log('  - socialMode:', socialMode);
    debug.log('  - isSaving (before):', isSaving);
    debug.log('='.repeat(60));
    
    // Guard: Already saving?
    if (isSaving) {
      debug.warn('[Onboarding] ⚠️ Already saving, ignoring duplicate call');
      return;
    }
    
    if (!authUser) {
      debug.error('[Onboarding] ❌ ABORT: No authUser');
      toast({
        title: "Error",
        description: "Not authenticated. Please sign in again.",
        variant: "destructive"
      });
      return;
    }

    if (!handle || handle.length < 3) {
      debug.error('[Onboarding] ❌ ABORT: Invalid handle:', handle);
      toast({
        title: "Invalid Handle",
        description: "Please set a valid handle (minimum 3 characters)",
        variant: "destructive"
      });
      setStep(1); // V4: Handle is step 1
      return;
    }

    if (!selectedTimezone) {
      debug.error('[Onboarding] ❌ ABORT: No timezone');
      toast({
        title: "Missing Timezone",
        description: "Please select your timezone",
        variant: "destructive"
      });
      setStep(3); // V4: Timezone is step 3
      return;
    }

    if (!selectedAvatar) {
      debug.error('[Onboarding] ❌ ABORT: No avatar');
      toast({
        title: "Missing Avatar",
        description: "Please select an avatar",
        variant: "destructive"
      });
      setStep(4); // V4: Avatar is step 4
      return;
    }
    
    debug.log('[Onboarding] ✅ All validation passed at +%dms', Date.now() - startTime);
    debug.log('[Onboarding] 🔒 Setting isSaving=true...');
    setIsSaving(true);
    
    try {
      // Step 1: Check existing profile
      debug.log('[Onboarding] 📡 Step 1: Checking if profile already completed...');
      const checkStart = Date.now();
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('onboarding_completed, handle, id')
        .eq('id', authUser.id)
        .maybeSingle();
      
      debug.log('[Onboarding] 📥 Profile check completed at +%dms:', Date.now() - startTime);
      debug.log('  - existingProfile:', JSON.stringify(existingProfile, null, 2));
      debug.log('  - checkError:', checkError);
      debug.log('  - query took:', Date.now() - checkStart, 'ms');
      
      if (checkError) {
        debug.error('[Onboarding] ❌ Error checking profile:', checkError);
        throw checkError;
      }
      
      if (existingProfile?.onboarding_completed) {
        // Path A: Already completed - use UPDATE
        debug.log('[Onboarding] 📝 Path A: Already completed - using direct UPDATE');
        const updateStart = Date.now();
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            handle: handle.toLowerCase(),
            avatar_seed: selectedAvatar,
            avatar_type: professionalMode ? 'initials' : 'dicebear',
            use_initials_avatar: professionalMode,
            daily_goal_minutes: totalDailyGoal,
            timezone: selectedTimezone,
            study_profile: socialMode ? 'social' : 'solo',
            theme: selectedTheme
          })
          .eq('id', authUser.id);
        
        debug.log('[Onboarding] 📥 UPDATE completed at +%dms:', Date.now() - startTime);
        debug.log('  - updateError:', updateError);
        debug.log('  - query took:', Date.now() - updateStart, 'ms');
        
        if (updateError) {
          debug.error('[Onboarding] ❌ UPDATE failed:', updateError);
          throw updateError;
        }
        debug.log('[Onboarding] ✅ Path A: UPDATE succeeded');
      } else {
        // Path B: First time - use RPC
        debug.log('[Onboarding] 📝 Path B: First time - using RPC complete_onboarding');
        const rpcParams = {
          p_handle: handle,
          p_avatar_seed: selectedAvatar,
          p_avatar_type: professionalMode ? 'initials' : 'dicebear',
          p_use_initials_avatar: professionalMode,
          p_daily_goal_minutes: totalDailyGoal,
          p_timezone: selectedTimezone,
          p_study_profile: socialMode ? 'social' : 'solo',
          p_theme: selectedTheme
        };
        debug.log('[Onboarding] 📤 RPC params:', JSON.stringify(rpcParams, null, 2));
        debug.log('[Onboarding] 🔄 Social mode will be saved as:', rpcParams.p_study_profile);
        debug.log('[Onboarding] 📊 socialMode state at save time:', socialMode);
        
        const rpcStart = Date.now();
        const { data, error } = await supabase.rpc('complete_onboarding', rpcParams);
        
        debug.log('[Onboarding] 📥 RPC completed at +%dms:', Date.now() - startTime);
        debug.log('  - data:', JSON.stringify(data, null, 2));
        debug.log('  - error:', error);
        debug.log('  - RPC took:', Date.now() - rpcStart, 'ms');
        
        if (error) {
          debug.error('[Onboarding] ❌ RPC error:', error);
          throw error;
        }
        
        const result = data as { success: boolean; error?: string; error_code?: string; handle?: string; avatar_type?: string; study_profile?: string; theme?: string };
        debug.log('[Onboarding] 🔍 Parsed result:', result);
        
        if (!result?.success) {
          debug.error('[Onboarding] ❌ RPC returned success=false');
          debug.error('  - error:', result?.error);
          debug.error('  - error_code:', result?.error_code);
          throw new Error(result?.error || 'Failed to save profile');
        }
        debug.log('[Onboarding] ✅ Path B: RPC succeeded with success=true');
      }

      // Step 2: Update AuthContext (this triggers UserContext profile load via effect)
      debug.log('[Onboarding] 🔄 Step 2: Calling setOnboardingCompleted(true)...');
      setOnboardingCompleted(true);
      debug.log('[Onboarding] ✅ setOnboardingCompleted(true) called at +%dms', Date.now() - startTime);

      // Step 3: Set theme - SINGLE authorized write per theme-architecture.md V2
      debug.log('[Onboarding] 🎨 Step 3: Setting theme to:', selectedTheme);
      themeDebug.applied('Onboarding.completeOnboarding', selectedTheme, 'Final confirmation');
      setTheme(selectedTheme);
      debug.log('[Onboarding] ✅ Theme set');
      
      // Step 4: Wait for state propagation (AuthContext will trigger UserContext profile load)
      debug.log('[Onboarding] ⏳ Step 4: Waiting 500ms for state propagation...');
      await new Promise(resolve => setTimeout(resolve, 500));
      debug.log('[Onboarding] ✅ Wait complete at +%dms', Date.now() - startTime);

      // Step 5: Dispatch profile-updated event to ensure immediate UI refresh (avatar)
      debug.log('[Onboarding] 👤 Step 5: Dispatching profile-updated event for avatar...');
      debug.log('  - selectedAvatar:', selectedAvatar);
      debug.log('  - avatarType:', professionalMode ? 'initials' : 'dicebear');
      debug.log('  - useInitialsAvatar:', professionalMode);
      window.dispatchEvent(new CustomEvent('profile-updated'));
      debug.log('[Onboarding] ✅ profile-updated event dispatched');

      // Step 8: Show toast
      debug.log('[Onboarding] 🎉 Step 8: Showing success toast');
      toast({
        title: "Profile Complete!",
        description: `Welcome, ${handle}! Let's start studying.`,
      });
      
      // Step 9: Track analytics
      debug.log('[Onboarding] 📊 Step 9: Tracking analytics event');
      trackEvent('onboarding_complete', {
        handle,
        daily_goal: totalDailyGoal,
        timezone: selectedTimezone,
        professional_mode: professionalMode,
        social_mode: socialMode,
        study_profile: socialMode ? 'social' : 'solo',
        avatar_type: professionalMode ? 'initials' : 'dicebear',
        theme: selectedTheme
      });
      
      // Step 10: Navigate
      debug.log('[Onboarding] 🚀 Step 10: Navigating to /timer...');
      debug.log('  - typeof navigate:', typeof navigate);
      debug.log('  - Total time:', Date.now() - startTime, 'ms');
      debug.log('='.repeat(60));
      navigate('/timer');
      debug.log('[Onboarding] ✅ navigate("/timer") called');
      
    } catch (error: any) {
      debug.error('='.repeat(60));
      debug.error('[Onboarding] ❌ CATCH BLOCK - Error occurred at +%dms:', Date.now() - startTime);
      debug.error('  - error:', error);
      debug.error('  - error.message:', error?.message);
      debug.error('  - error.code:', error?.code);
      debug.error('  - error.details:', error?.details);
      debug.error('  - error.hint:', error?.hint);
      debug.error('  - Full error:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      debug.error('='.repeat(60));
      
      toast({
        title: "Error Saving Profile",
        description: error.message || "Please try again.",
        variant: "destructive"
      });
    } finally {
      debug.log('[Onboarding] 🔓 FINALLY: Setting isSaving=false');
      setIsSaving(false);
    }
  };

  if (loadingHandle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // V4: Derived state for handle step
  const canContinueHandle = handleStatus === 'valid' && handleInputValue.length >= HANDLE_MIN_LENGTH;

  const renderStep = () => {
    switch (step) {
      // V4: Step 1 = Handle Selection (always shown)
      case 1:
        return (
          <Card className="max-w-md mx-auto p-8 text-center shadow-card">
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AtSign className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Choose Your Handle</h2>
                <p className="text-muted-foreground">
                  This is how others will find you on StudyForge
                </p>
              </div>

              <div className="space-y-3">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    @
                  </div>
                  <Input
                    value={handleInputValue}
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                      setHandleInputValue(value);
                      setHandle(value);
                      validateHandle(value, originalDbHandle); // Pass original handle to allow keeping it
                    }}
                    placeholder="yourhandle"
                    className={cn(
                      "pl-8 pr-10 h-12 text-lg",
                      handleStatus === 'valid' && "border-green-500 focus-visible:ring-green-500",
                      (handleStatus === 'invalid' || handleStatus === 'taken') && "border-destructive focus-visible:ring-destructive"
                    )}
                    maxLength={HANDLE_MAX_LENGTH}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {handleStatus === 'checking' && (
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    )}
                    {handleStatus === 'valid' && (
                      <Check className="w-5 h-5 text-green-500" />
                    )}
                    {(handleStatus === 'invalid' || handleStatus === 'taken') && handleInputValue && (
                      <X className="w-5 h-5 text-destructive" />
                    )}
                  </div>
                </div>
                
                {/* Status message */}
                <div className={cn(
                  "text-sm transition-colors",
                  handleStatus === 'valid' && "text-green-500",
                  (handleStatus === 'invalid' || handleStatus === 'taken') && "text-destructive",
                  handleStatus === 'checking' && "text-muted-foreground"
                )}>
                  {handleMessage || `${HANDLE_MIN_LENGTH}-${HANDLE_MAX_LENGTH} characters, letters, numbers, underscores`}
                </div>
                
                {/* V4: Subtle hint when handle is valid */}
                {handleStatus === 'valid' && (
                  <p className="text-xs text-muted-foreground">
                    You can change this later in Profile
                  </p>
                )}
              </div>

              <Button 
                onClick={() => {
                  debug.log('[Onboarding:HandleStep] Handle confirmed:', handleInputValue);
                  nextStep();
                }}
                disabled={!canContinueHandle}
                className="w-full h-12 active:scale-[0.98] transition-transform"
              >
                Continue
              </Button>
              
              {/* FAIL-OPEN ESCAPE HATCH: Allow users to proceed even if validation hangs */}
              {(handleStatus === 'checking' || handleStatus === 'invalid') && handleInputValue.length >= HANDLE_MIN_LENGTH && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    debug.log('[Onboarding:HandleStep] User skipping handle validation, using current handle');
                    setHandle(handleInputValue);
                    nextStep();
                  }}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  Continue with current handle
                </Button>
              )}
            </div>
          </Card>
        );
      
      // V4: Step 2 = Daily Goal (was Step 1)
      case 2:
        return (
          <Card className="max-w-md mx-auto p-8 text-center shadow-card">
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Set Your Daily Goal</h2>
                <p className="text-muted-foreground">
                  How much do you want to study each day?
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Daily Study Goal</Label>
                  <div className="flex gap-2 mt-2">
                    <select 
                      className="flex-1 h-12 text-lg text-center border border-input bg-background rounded-md px-3 focus:ring-2 focus:ring-primary/50 transition-all"
                      value={dailyGoalHours}
                      onChange={(e) => {
                        const hours = parseInt(e.target.value);
                        setDailyGoalHours(hours);
                        const newTotal = hours * 60 + dailyGoalMinutes;
                        if (newTotal > 480) {
                          trackEvent('onboarding_premium_goal_selected', {
                            hours,
                            minutes: dailyGoalMinutes,
                            total_minutes: newTotal
                          });
                        }
                      }}
                    >
                      {Array.from({length: 17}, (_, i) => (
                        <option key={i} value={i}>{i} hours</option>
                      ))}
                    </select>
                    <select 
                      className="flex-1 h-12 text-lg text-center border border-input bg-background rounded-md px-3 focus:ring-2 focus:ring-primary/50 transition-all"
                      value={dailyGoalMinutes}
                      onChange={(e) => setDailyGoalMinutes(parseInt(e.target.value))}
                    >
                      <option value={0}>0 min</option>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                    </select>
                  </div>
                  
                  {/* Enhanced Goal Display */}
                  <div className={cn(
                    "text-center p-6 rounded-xl mt-4 transition-all duration-300",
                    isPremiumGoal 
                      ? "bg-primary/10 border-2 border-primary/40" 
                      : "bg-primary/5 border border-primary/20"
                  )}>
                    <div className="text-5xl font-bold text-primary tracking-tight">
                      {Math.floor(totalDailyGoal / 60)}h {totalDailyGoal % 60 > 0 && `${totalDailyGoal % 60}m`}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">per day</p>
                  </div>
                  
                  {totalDailyGoal < 15 && (
                    <p className="text-destructive text-sm mt-2 flex items-center justify-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Minimum 15 minutes required
                    </p>
                  )}

                  {isPremiumGoal && (
                    <div className="flex items-center justify-center gap-2 p-3 bg-primary/10 border border-primary/30 rounded-lg mt-3">
                      <Crown className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-primary">Premium Feature</span>
                    </div>
                  )}
                </div>
              </div>

              {isPremiumGoal ? (
                <div className="space-y-3">
                  <Button 
                    onClick={() => {
                      trackEvent('onboarding_premium_upsell_clicked', {
                        goal_attempted: totalDailyGoal
                      });
                      setShowPremiumUpsell(true);
                    }} 
                    className="w-full h-12 active:scale-[0.98] transition-transform"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Premium
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => {
                      trackEvent('onboarding_premium_upsell_declined', {
                        goal_attempted: totalDailyGoal,
                        reason: 'chose_free_limit'
                      });
                      setDailyGoalHours(8);
                      setDailyGoalMinutes(0);
                      nextStep();
                    }}
                    className="w-full text-xs"
                  >
                    Continue with Free (8h max)
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={nextStep} 
                  disabled={totalDailyGoal < 15}
                  className="w-full h-12 active:scale-[0.98] transition-transform"
                >
                  Continue
                </Button>
              )}
              
              {/* V4: Back button to Handle step */}
              <button 
                onClick={prevStep}
                className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-all flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            </div>
          </Card>
        );
      
      // V4: Step 3 = Timezone (was Step 2)
      case 3:
        return (
          <Card className="max-w-md mx-auto p-8 text-center shadow-card">
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Your Timezone</h2>
                <p className="text-muted-foreground">
                  This helps us track your daily progress correctly
                </p>
              </div>

              {/* Auto-detected timezone banner */}
              <div className="flex items-center justify-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">
                  Detected: <span className="text-primary">{detectedTimezone.split('/').pop()?.replace('_', ' ')}</span>
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Select Your Timezone</Label>
                  <select 
                    className="w-full h-12 text-sm border border-input bg-background rounded-md px-3 mt-2 focus:ring-2 focus:ring-primary/50 transition-all"
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                  >
                    {/* Americas */}
                    <option value="America/New_York">Eastern Time (America/New_York)</option>
                    <option value="America/Chicago">Central Time (America/Chicago)</option>
                    <option value="America/Denver">Mountain Time (America/Denver)</option>
                    <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
                    <option value="America/Phoenix">Arizona Time (America/Phoenix)</option>
                    <option value="America/Anchorage">Alaska Time (America/Anchorage)</option>
                    <option value="Pacific/Honolulu">Hawaii Time (Pacific/Honolulu)</option>
                    <option value="America/Toronto">Toronto (America/Toronto)</option>
                    <option value="America/Vancouver">Vancouver (America/Vancouver)</option>
                    <option value="America/Mexico_City">Mexico City (America/Mexico_City)</option>
                    <option value="America/Sao_Paulo">São Paulo (America/Sao_Paulo)</option>
                    <option value="America/Buenos_Aires">Buenos Aires (America/Buenos_Aires)</option>
                    <option value="America/Lima">Lima (America/Lima)</option>
                    
                    {/* Europe */}
                    <option value="Europe/London">London (Europe/London)</option>
                    <option value="Europe/Paris">Paris (Europe/Paris)</option>
                    <option value="Europe/Berlin">Berlin (Europe/Berlin)</option>
                    <option value="Europe/Rome">Rome (Europe/Rome)</option>
                    <option value="Europe/Madrid">Madrid (Europe/Madrid)</option>
                    <option value="Europe/Amsterdam">Amsterdam (Europe/Amsterdam)</option>
                    <option value="Europe/Moscow">Moscow (Europe/Moscow)</option>
                    <option value="Europe/Istanbul">Istanbul (Europe/Istanbul)</option>
                    <option value="Europe/Warsaw">Warsaw (Europe/Warsaw)</option>
                    <option value="Europe/Athens">Athens (Europe/Athens)</option>
                    
                    {/* Africa */}
                    <option value="Africa/Cairo">Cairo (Africa/Cairo)</option>
                    <option value="Africa/Lagos">Lagos (Africa/Lagos)</option>
                    <option value="Africa/Johannesburg">Johannesburg (Africa/Johannesburg)</option>
                    <option value="Africa/Nairobi">Nairobi (Africa/Nairobi)</option>
                    
                    {/* Middle East */}
                    <option value="Asia/Dubai">Dubai (Asia/Dubai)</option>
                    <option value="Asia/Riyadh">Riyadh (Asia/Riyadh)</option>
                    <option value="Asia/Jerusalem">Jerusalem (Asia/Jerusalem)</option>
                    
                    {/* Asia */}
                    <option value="Asia/Tokyo">Tokyo (Asia/Tokyo)</option>
                    <option value="Asia/Shanghai">Shanghai (Asia/Shanghai)</option>
                    <option value="Asia/Kolkata">India (Asia/Kolkata)</option>
                    <option value="Asia/Singapore">Singapore (Asia/Singapore)</option>
                    <option value="Asia/Bangkok">Bangkok (Asia/Bangkok)</option>
                    <option value="Asia/Jakarta">Jakarta (Asia/Jakarta)</option>
                    <option value="Asia/Manila">Manila (Asia/Manila)</option>
                    <option value="Asia/Seoul">Seoul (Asia/Seoul)</option>
                    <option value="Asia/Hong_Kong">Hong Kong (Asia/Hong_Kong)</option>
                    
                    {/* Oceania */}
                    <option value="Australia/Sydney">Sydney (Australia/Sydney)</option>
                    <option value="Australia/Melbourne">Melbourne (Australia/Melbourne)</option>
                    <option value="Pacific/Auckland">Auckland (Pacific/Auckland)</option>
                    <option value="Pacific/Fiji">Fiji (Pacific/Fiji)</option>
                    <option value="Pacific/Guam">Guam (Pacific/Guam)</option>
                    
                    {/* UTC */}
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <Button onClick={nextStep} className="w-full h-12 active:scale-[0.98] transition-transform">
                  Continue
                </Button>
                <button 
                  onClick={prevStep}
                  className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-all flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              </div>
            </div>
          </Card>
        );
      
      // V4: Step 4 = Avatar & Profile (was Step 3)
      case 4:
        return (
          <Card className="max-w-md mx-auto p-8 shadow-card">
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Avatar & Profile</h2>
                <p className="text-muted-foreground">
                  Customize how you appear to others
                </p>
              </div>

              {/* Avatar Preview */}
              <div className="flex flex-col items-center gap-3">
                <div className={cn(
                  "relative transition-transform duration-300",
                  professionalMode ? "scale-95" : "scale-100"
                )}>
                  <img
                    src={getAvatarUrl(selectedAvatar, professionalMode, handle)}
                    alt="Avatar"
                    className="rounded-full w-24 h-24 border-4 border-primary/20 shadow-lg"
                  />
                  {professionalMode && (
                    <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-1.5 shadow-md">
                      <UserSquare2 className="w-4 h-4" />
                    </div>
                  )}
                </div>
                <span className="text-lg font-semibold">@{handle}</span>
                
                {!professionalMode && (
                  <div className="space-y-1 text-center">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAvatarSelector(true)}
                      className="gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Change Avatar
                    </Button>
                  </div>
                )}
              </div>

              {/* Mode Toggles */}
              <div className="space-y-4">
                {/* Professional Mode Toggle */}
                <div 
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all cursor-pointer",
                    professionalMode 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/30"
                  )}
                  onClick={() => setProfessionalMode(!professionalMode)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "p-2 rounded-lg transition-colors",
                        professionalMode ? "bg-primary/20" : "bg-muted"
                      )}>
                        <UserSquare2 className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">Professional Mode</span>
                        <p className="text-xs text-muted-foreground">Use your initials as avatar</p>
                      </div>
                    </div>
                    <Switch 
                      checked={professionalMode} 
                      onCheckedChange={setProfessionalMode}
                    />
                  </div>
                </div>

                {/* Social Mode Toggle */}
                <div 
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all cursor-pointer",
                    socialMode 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/30"
                  )}
                  onClick={() => setSocialMode(!socialMode)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "p-2 rounded-lg transition-colors",
                        socialMode ? "bg-primary/20" : "bg-muted"
                      )}>
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">Social Mode</span>
                        <p className="text-xs text-muted-foreground">Appear on leaderboards and discover friends</p>
                      </div>
                    </div>
                    <Switch 
                      checked={socialMode} 
                      onCheckedChange={setSocialMode}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <Button onClick={nextStep} className="w-full h-12 active:scale-[0.98] transition-transform">
                  Continue
                </Button>
                <button 
                  onClick={prevStep}
                  className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-all flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              </div>
            </div>
          </Card>
        );
      
      // V4: Step 5 = Theme (was Step 4)
      case 5:
        const currentPreviewTheme = getThemeById(selectedTheme);
        return (
          <Card className="max-w-md mx-auto p-8 shadow-card">
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Palette className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Pick Your Vibe</h2>
                <p className="text-muted-foreground">
                  Choose a theme that suits your style
                </p>
              </div>

              {/* Sandboxed Theme Preview Card - shows what the app looks like */}
              {/* INVARIANT: Uses inline styles only, no global CSS mutation */}
              <ThemePreviewCard 
                theme={currentPreviewTheme} 
                className="animate-in fade-in-50 duration-300"
              />

              {/* Free Themes Section */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">Free Themes</p>
                <div className="space-y-2">
                  {getFreeThemes().map((themeOption) => (
                    <div
                      key={themeOption.id}
                      onClick={() => {
                        // V2: Preview only - state change, NO setTheme()
                        // Per theme-architecture.md: Theme applied once in completeOnboarding()
                        setSelectedTheme(themeOption.id);
                        debug.log('[Onboarding:Theme] Preview selected (state only):', themeOption.id);
                      }}
                      className={cn(
                        "p-4 rounded-xl border-2 cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99]",
                        selectedTheme === themeOption.id 
                          ? "border-primary bg-primary/5 shadow-md" 
                          : "border-border hover:border-primary/50 hover:shadow-sm"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1.5">
                          <div className={cn(
                            "w-6 h-6 rounded-full border border-border/50 shadow-sm",
                            themeOption.previewColors.bg
                          )} />
                          <div className={cn(
                            "w-6 h-6 rounded-full border border-border/50 shadow-sm",
                            themeOption.previewColors.card
                          )} />
                          <div className={cn(
                            "w-6 h-6 rounded-full border border-border/50 shadow-sm",
                            themeOption.previewColors.primary
                          )} />
                        </div>
                        
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{themeOption.name}</span>
                            {selectedTheme === themeOption.id && (
                              <CheckCircle className="w-4 h-4 text-primary" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{themeOption.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Premium Themes Section */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Premium Themes
                </p>
                <div className="space-y-2">
                  {getPremiumThemes().map((themeOption) => {
                    const isSelected = selectedTheme === themeOption.id;
                    return (
                      <div
                        key={themeOption.id}
                        onClick={() => {
                          setSelectedTheme(themeOption.id);
                          trackEvent('onboarding_theme_premium_previewed', { theme: themeOption.id });
                        }}
                        className={cn(
                          "p-4 rounded-xl border-2 cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99]",
                          isSelected 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/30"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1.5">
                            <div className={cn(
                              "w-6 h-6 rounded-full border border-border/50",
                              themeOption.previewColors.bg
                            )} />
                            <div className={cn(
                              "w-6 h-6 rounded-full border border-border/50",
                              themeOption.previewColors.card
                            )} />
                            <div className={cn(
                              "w-6 h-6 rounded-full border border-border/50",
                              themeOption.previewColors.primary
                            )} />
                          </div>
                          
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{themeOption.name}</span>
                              {isSelected && <Check className="w-4 h-4 text-primary" />}
                              <Crown className="w-3.5 h-3.5 text-amber-500" />
                            </div>
                            <p className="text-xs text-muted-foreground">{themeOption.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation with Continue Gate */}
              <div className="space-y-3 pt-2">
                {(() => {
                  const isPremiumThemeSelected = getThemeById(selectedTheme).isPremium;
                  
                  if (isPremiumThemeSelected) {
                    return (
                      <>
                        <p className="text-xs text-center text-muted-foreground">
                          Premium themes require upgrade to apply
                        </p>
                        <Button 
                          onClick={() => {
                            trackEvent('onboarding_theme_premium_continue_clicked', { theme: selectedTheme });
                            setShowPremiumUpsell(true);
                          }} 
                          className="w-full h-12 active:scale-[0.98] transition-transform"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Premium
                        </Button>
                        <button 
                          onClick={() => {
                            trackEvent('onboarding_theme_free_fallback_used', { 
                              theme_attempted: selectedTheme,
                              fallback: 'charcoal-pro'
                            });
                            setSelectedTheme('charcoal-pro');
                            nextStep();
                          }}
                          className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-all"
                        >
                          Continue with Free Theme
                        </button>
                      </>
                    );
                  }
                  
                  return (
                    <Button onClick={nextStep} className="w-full h-12 active:scale-[0.98] transition-transform">
                      Continue
                    </Button>
                  );
                })()}
                <button 
                  onClick={prevStep}
                  className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-all flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              </div>
            </div>
          </Card>
        );
      
      // V4: Step 6 = Summary (was Step 5)
      case 6:
        const selectedThemeData = getThemeById(selectedTheme);
        return (
          <Card className="max-w-md mx-auto p-8 shadow-card">
            <div className="space-y-6">
              {/* Celebration Header */}
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-scale-in">
                  <CheckCircle className="w-10 h-10 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-1">🎉 You're All Set!</h2>
                <p className="text-muted-foreground">
                  Review your settings before starting
                </p>
              </div>

              {/* Avatar + Handle Section */}
              <div className="flex flex-col items-center gap-2 p-4 bg-primary/5 rounded-xl border border-primary/20">
                <img 
                  src={getAvatarUrl(selectedAvatar, professionalMode, handle)} 
                  alt="Avatar" 
                  className="w-20 h-20 rounded-full border-4 border-primary/20 shadow-lg" 
                />
                <span className="text-lg font-semibold">@{handle}</span>
              </div>

              {/* Settings Summary */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Daily Goal</span>
                  </div>
                  <span className="font-medium">{Math.floor(totalDailyGoal / 60)}h {totalDailyGoal % 60 > 0 && `${totalDailyGoal % 60}m`}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Timezone</span>
                  </div>
                  <span className="font-medium text-xs">{selectedTimezone.split('/').pop()?.replace('_', ' ')}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Social Mode</span>
                  </div>
                  <Badge variant={socialMode ? "default" : "outline"}>
                    {socialMode ? "On" : "Off"}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Theme</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedThemeData && (
                      <div className="flex gap-1">
                        <div className={cn("w-3 h-3 rounded-full", selectedThemeData.previewColors.primary)} />
                      </div>
                    )}
                    <span className="font-medium text-sm">{selectedThemeData?.name || selectedTheme}</span>
                  </div>
                </div>
              </div>

              {/* Premium Upsell Card */}
              <div 
                onClick={() => setShowPremiumUpsell(true)}
                className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 cursor-pointer hover:brightness-110 active:scale-[0.99] transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-sm">Unlock Premium Features</span>
                    <p className="text-xs text-muted-foreground">Extended goals, premium themes & more</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <Button 
                  onClick={completeOnboarding} 
                  disabled={isSaving} 
                  className="w-full h-12 active:scale-[0.98] transition-transform"
                >
                  {isSaving ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                      Starting...
                    </div>
                  ) : (
                    "Start Studying"
                  )}
                </Button>
                <button 
                  onClick={prevStep}
                  className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-all flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              </div>
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pt-12 pb-24 px-4">
      <div className="absolute top-4 right-4">
        <Button variant="ghost" size="sm" onClick={handleSignOut}>
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>

      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header with animation */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Welcome to StudyForge</h1>
          <p className="text-muted-foreground">Let's personalize your experience</p>
        </div>

        {/* V4: Always 6-step progress bar */}
        <div className="flex justify-center gap-2 mb-8">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <div
              key={s}
              className={cn(
                "h-2 w-10 rounded-full transition-all duration-500",
                step >= s 
                  ? "bg-primary shadow-sm" 
                  : "bg-muted",
                step === s && "shadow-[0_0_8px_hsl(var(--primary)/0.5)]"
              )}
            />
          ))}
        </div>

        {/* Animated Step Container */}
        <div 
          className={cn(
            "transition-all duration-300 ease-out",
            isAnimating && stepDirection === 'forward' && "opacity-0 translate-x-4",
            isAnimating && stepDirection === 'backward' && "opacity-0 -translate-x-4",
            !isAnimating && "opacity-100 translate-x-0"
          )}
        >
          {renderStep()}
        </div>

        {showAvatarSelector && (
          <PixelAvatarSelector
            currentAvatar={selectedAvatar}
            onAvatarChange={(seed) => {
              setSelectedAvatar(seed);
              setShowAvatarSelector(false);
            }}
            isOpen={showAvatarSelector}
            onClose={() => setShowAvatarSelector(false)}
          />
        )}

        {showPremiumUpsell && (
          <PremiumUpgradeModal 
            isOpen={showPremiumUpsell} 
            onClose={() => setShowPremiumUpsell(false)}
          />
        )}
      </div>
    </div>
  );
}
